import React, { useState } from 'react'
import { Navigate, useLocation, Link, useNavigate } from 'react-router-dom'
import { useAuth, UserRole } from '../contexts/AuthContext'
import {
  BuildingOfficeIcon,
  TruckIcon,
  DocumentMagnifyingGlassIcon,
  BuildingStorefrontIcon,
  CogIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline'
import { ESGLedgerLogo } from '../components/ESGLedgerLogo'

const Login: React.FC = () => {
  const { login, isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'gov' as UserRole
  })
  const [error, setError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)

  // Redirect if already authenticated - use actual user's role, not form role
  if (isAuthenticated && user) {
    const from = location.state?.from?.pathname || getDefaultRedirect(user.role)
    return <Navigate to={from} replace />
  }

  const roles = [
    {
      value: 'gov' as UserRole,
      label: 'Government Regulator',
      icon: BuildingOfficeIcon,
      color: 'from-blue-500 to-indigo-600',
      description: 'Environmental Protection Department'
    },
    {
      value: 'suppliers' as UserRole,
      label: 'Supplier (Corporate Client)',
      icon: TruckIcon,
      color: 'from-emerald-500 to-teal-600',
      description: 'Recycling data suppliers'
    },
    {
      value: 'auditors' as UserRole,
      label: 'Independent Auditor',
      icon: DocumentMagnifyingGlassIcon,
      color: 'from-purple-500 to-violet-600',
      description: 'Compliance verification'
    },
    {
      value: 'retailers' as UserRole,
      label: 'Retailer',
      icon: BuildingStorefrontIcon,
      color: 'from-orange-500 to-amber-600',
      description: 'Retail chain management'
    },
    {
      value: 'admin' as UserRole,
      label: 'System Administrator',
      icon: CogIcon,
      color: 'from-gray-500 to-slate-600',
      description: 'Full system access'
    }
  ]

  const demoCredentials: Partial<Record<UserRole, { username: string; password: string }>> = {
    admin: { username: 'admin', password: 'admin123' },
    gov: { username: 'gov_user', password: 'gov123' },
    suppliers: { username: 'enterprise_supplier', password: 'supplier123' },
    auditors: { username: 'auditor_user', password: 'audit123' },
    retailers: { username: 'retailer_user', password: 'retail123' }
  }

  function getDefaultRedirect(role: UserRole): string {
    switch (role) {
      case 'admin':
        return '/admin'
      case 'gov':
        return '/regulators'
      case 'suppliers':
        return '/suppliers'
      case 'auditors':
        return '/auditors'
      case 'retailers':
        return '/retailers'
      default:
        return '/'
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoginLoading(true)

    try {
      const success = await login(formData.username, formData.password, formData.role)
      if (success) {
        // Immediately redirect to the correct dashboard after successful login
        const redirectPath = getDefaultRedirect(formData.role)
        navigate(redirectPath, { replace: true })
      } else {
        setError('Invalid credentials. Please check your username, password, and role.')
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setLoginLoading(false)
    }
  }

  const handleDemoLogin = (role: UserRole) => {
    const credentials = demoCredentials[role]
    if (credentials) {
      setFormData({
        username: credentials.username,
        password: credentials.password,
        role: role
      })
    }
  }

  const selectedRole = roles.find(role => role.value === formData.role)

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <ESGLedgerLogo className="h-14 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900">Sign in to ESGLedger</h2>
          <p className="mt-2 text-gray-600">Blockchain Recycling Reporting System</p>
        </div>

        {/* Role Selection */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Select Your Role</h3>
          <div className="space-y-3">
            {roles.map((role) => (
              <label key={role.value} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="role"
                  value={role.value}
                  checked={formData.role === role.value}
                  onChange={(e) => setFormData({...formData, role: e.target.value as UserRole})}
                  className="sr-only"
                />
                <div className={`flex items-center w-full p-3 rounded-lg border-2 transition-all ${
                  formData.role === role.value 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <div className={`w-10 h-10 bg-gradient-to-r ${role.color} rounded-lg flex items-center justify-center mr-3`}>
                    <role.icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{role.label}</div>
                    <div className="text-sm text-gray-500">{role.description}</div>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                id="username"
                type="text"
                required
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter your username"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter your password"
              />
            </div>

            {error && (
              <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-md">
                <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loginLoading || isLoading}
              className={`w-full py-2 px-4 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                selectedRole 
                  ? `bg-gradient-to-r ${selectedRole.color} text-white hover:opacity-90` 
                  : 'bg-gray-400 text-white cursor-not-allowed'
              } ${loginLoading || isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {loginLoading || isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Return to Main Page Button */}
          <div className="mt-4 text-center">
            <Link
              to="/"
              className="text-primary-600 hover:text-primary-700 text-sm font-medium transition-colors"
            >
              ← Return to Main Page
            </Link>
          </div>
        </div>

        {/* Demo Credentials */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Demo Credentials</h3>
          <p className="text-sm text-gray-600 mb-4">Click to auto-fill credentials for testing:</p>
          <div className="grid grid-cols-1 gap-2">
            {roles.map((role) => {
              const credentials = demoCredentials[role.value]
              return credentials ? (
                <button
                  key={role.value}
                  type="button"
                  onClick={() => handleDemoLogin(role.value)}
                  className="text-left p-2 rounded border hover:bg-gray-50 transition-colors"
                >
                  <div className="font-medium text-sm">{role.label}</div>
                  <div className="text-xs text-gray-500">
                    {credentials.username} / {credentials.password}
                  </div>
                </button>
              ) : null
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
